/**
 * The stops the World agent was told about, each with what it decided (RFC
 * 0348 3e). Folded from the World agent's own log: a `mod.session_stopped`
 * is a report, and the next `mod.stop_decided` naming the same session after
 * it is the decision. Nothing is stored beyond the two events.
 */

import { pageSeedOf } from './page-seed'
import { topoOrder } from './projection-order'
import type { LogDoc, StopVerdict } from './types'
import { SESSION_STOPPED_EXTENSION, STOP_DECIDED_EXTENSION } from './types'

export interface StopReport {
  /** The report's entry, the same id its transcript message carries. */
  entryId: string
  sessionId: string
  /** The run that stopped, and the question it asked: with `status`, which
   *  stop this is. A stop heard twice (its first turn was cut short) is one
   *  report, the later hearing. */
  runId: string
  interactionId: string
  status: string
  summary: string
  preview: string
  at: number
  /** The person had it open (3b): nothing to decide, so never a verdict. */
  seen: boolean
  /** Absent until the World agent decides. */
  verdict?: StopVerdict
  note?: string
}

const VERDICTS: ReadonlySet<string> = new Set(['answered', 'yours', 'nothing'])

/**
 * When the turn that made a decision began, or the decision's own time when
 * no turn was running then (a test, or a tool called outside a turn).
 *
 * A turn decides what it was prompted with. A report that lands while it runs
 * is news it never read, even from a session it is deciding about: the loop
 * it just answered, stopping again. So a decision covers the reports that were
 * on the log when its turn began, and a later one stays owed (3c).
 */
function turnStarts(doc: LogDoc): (at: number) => number {
  const runs: { start: number; end: number }[] = []
  for (const e of Object.values(doc.entries ?? {})) {
    if (e.payload.type !== 'run') continue
    const run = e.payload.payload as {
      status?: unknown
      startedAt?: number
      completedAt?: number
    }
    if (!run.startedAt) continue
    const status = String(run.status ?? '')
    const live = status === 'running' || status === 'waiting_for_tools'
    runs.push({
      start: run.startedAt,
      end: run.completedAt ?? (live ? Number.POSITIVE_INFINITY : run.startedAt),
    })
  }
  return (at) => {
    let start = at
    for (const r of runs) {
      if (r.start <= at && at <= r.end) start = Math.min(start, r.start)
    }
    return start
  }
}

export function projectStopReports(doc: LogDoc): StopReport[] {
  const out: StopReport[] = []
  /** When each report landed on this log, by entry. */
  const landed = new Map<string, number>()
  // A paged log (RFC 0360): the page's seed is the fold over every page
  // before it, so this starts from it and reads only this page's entries.
  const seed = pageSeedOf(doc)
  if (seed) {
    for (const r of seed.stopReports) {
      const { landedAt, ...row } = r
      out.push({
        ...row,
        ...(row.verdict ? { verdict: row.verdict as StopVerdict } : {}),
      } as StopReport)
      landed.set(row.entryId, landedAt)
    }
  }
  const turnStartAt = turnStarts(doc)
  for (const e of topoOrder(doc)) {
    const p = e.payload
    if (p.type !== 'extension') continue
    const d = p.payload.payload as Record<string, unknown>
    if (p.payload.extensionType === SESSION_STOPPED_EXTENSION) {
      out.push({
        entryId: String(e.id),
        sessionId: String(d.sessionId ?? ''),
        runId: String(d.runId ?? ''),
        interactionId: String(d.interactionId ?? ''),
        status: String(d.status ?? ''),
        summary: String(d.summary ?? ''),
        preview: String(d.preview ?? '') || String(d.summary ?? ''),
        at: Number(d.at ?? e.createdAt),
        seen: Boolean(d.seen),
      })
      landed.set(String(e.id), e.createdAt)
    } else if (p.payload.extensionType === STOP_DECIDED_EXTENSION) {
      const verdict = String(d.verdict ?? '')
      if (!VERDICTS.has(verdict)) continue
      const sessionId = String(d.sessionId ?? '')
      const runId = String(d.runId ?? '')
      const cutoff = turnStartAt(e.createdAt)
      for (const r of out) {
        if (r.sessionId !== sessionId || r.verdict || r.seen) continue
        if (r.status === 'started') continue
        // A decision that names its run covers that run's stops, however
        // often they were heard. One that does not (written before it could)
        // covers what its turn could read: two stops from one session in one
        // burst get one decision between them.
        if (runId) {
          if (r.runId !== runId) continue
        } else if ((landed.get(r.entryId) ?? 0) > cutoff) continue
        r.verdict = verdict as StopVerdict
        r.note = String(d.note ?? '')
      }
    }
  }
  // A stop heard again is one report: the latest hearing, with any verdict
  // an earlier one got.
  const latest = new Map<string, StopReport>()
  for (const r of out) {
    // A report with no run (written before reports carried one) stands alone.
    const key = r.runId
      ? `${r.sessionId}:${r.runId}:${r.status}:${r.interactionId}`
      : r.entryId
    const before = latest.get(key)
    latest.set(
      key,
      before?.verdict && !r.verdict
        ? {
            ...r,
            verdict: before.verdict,
            ...(before.note ? { note: before.note } : {}),
          }
        : r,
    )
  }
  return [...latest.values()]
}

/** How many times each stop was heard, by `sessionId:runId:status:interactionId`. */
export function stopHearings(doc: LogDoc): Map<string, number> {
  const n = new Map<string, number>()
  const seed = pageSeedOf(doc)
  if (seed) for (const [k, v] of Object.entries(seed.stopHearings)) n.set(k, v)
  for (const e of Object.values(doc.entries ?? {})) {
    const p = e.payload
    if (p.type !== 'extension') continue
    if (p.payload.extensionType !== SESSION_STOPPED_EXTENSION) continue
    const d = p.payload.payload as Record<string, unknown>
    const key = `${String(d.sessionId ?? '')}:${String(d.runId ?? '')}:${String(d.status ?? '')}:${String(d.interactionId ?? '')}`
    n.set(key, (n.get(key) ?? 0) + 1)
  }
  return n
}
